import {
  Component,
  ElementRef,
  forwardRef,
  Input,
  output,
  signal,
  WritableSignal
} from '@angular/core';
import { UploaderAttachmentsService } from '../../services/uploader-attachments.service';
import { AttachmentAttachmentRead } from '../../openapi';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { bytesToMb, FileSizePipe } from '../../pipes/file-size.pipe';

@Component({
  selector: 'app-file-uploader',
  standalone: true,
  imports: [
    CommonModule,
    FileSizePipe
  ],
  providers: [
    UploaderAttachmentsService,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FileUploaderComponent),
      multi: true
    }
  ],
  templateUrl: './file-uploader.component.html'
})
export class FileUploaderComponent implements ControlValueAccessor {

  @Input()
  public maxFileSize: number = 20;

  @Input()
  public tagName: string = '';

  @Input()
  public customName: string = '';

  @Input()
  public inputLabel: string | null = 'Nazwa pliku';

  @Input()
  public accept = '.jpg, .bmp, .png, .pdf';

  @Input()
  public multiple = false;

  @Input()
  public readonly = false;

  public pending = output<boolean>();

  public attachments: WritableSignal<Array<AttachmentAttachmentRead | undefined>> = signal([]);

  public disabled = false;

  public canAdd = true;

  public overSizeError = false;

  public onChange: any = () => {
  };

  public onTouch: any = () => {
  };

  constructor(
    private uploaderService: UploaderAttachmentsService,
    private elRef: ElementRef
  ) {
  }

  public download(event: Event, item: AttachmentAttachmentRead | undefined): void {
    event.preventDefault();
    event.stopPropagation();
    this.pending.emit(true);
    this.uploaderService.downloadAttachment(item?.id || '', item?.customName || item?.originalName).subscribe({
      complete: () => {
        this.pending.emit(false);
      }
    });
  }

  public isInvalid(): boolean {
    const list: DOMTokenList = this.elRef.nativeElement.classList;
    return list.contains('ng-invalid')
      && list.contains('ng-touched');
  }

  public writeValue(attachments?: AttachmentAttachmentRead[]) {
    if (!!attachments) {
      this.attachments.set(attachments);
    }
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public onInputChange(event: any): void {
    const files: FileList = event.target['files'];
    const overSize: boolean = !!Array.from(files || []).map(v => bytesToMb(v.size)).filter(v => v > this.maxFileSize).length;
    if (overSize) {
      this.overSizeError = true;
      return;
    }
    this.overSizeError = false;
    if (files?.length) {
      this.upload(files);
    }
  }

  private upload(files: FileList): void {
    this.pending.emit(true);
    forkJoin(Array.from(files).map((file: File) =>
      this.uploaderService.uploadAttachment({
        file,
        tag: this.tagName,
        customName: `${this.customName}.${file.name.split('.').reverse().at(0)}`
      })
    )).subscribe({
      next: (attachmentAttachmentReads?: AttachmentAttachmentRead[]) => {
        if (this.multiple) {
          this.attachments.update(attachments => [...attachments, ...(attachmentAttachmentReads || [])]);
        } else {
          this.attachments.set(attachmentAttachmentReads || []);
          this.canAdd = !this.attachments()?.length;
        }
        this.onChange(this.attachments());
      },
      complete: () => {
        this.pending.emit(false);
      }
    });
  }

  public onRemove(i: number): void {
    this.attachments.update(attachments => {
      attachments.splice(i, 1);
      return attachments;
    });
    if (!this.multiple) {
      this.canAdd = !this.attachments()?.length;
    }
    this.onChange(this.attachments());
  }
}
